import Modal from '../../../ui/Modal'
import { DataTable } from '../../../ui/DataTable'
import Button from '../../../ui/Button'
import Tag from '../../../ui/Tag'
import Menu, { MenuItem } from '../../../ui/Menu'
import { RestartIcon, TrashIcon } from '../../../ui/icons'
import { useContext, useEffect, useState } from 'react'
import api from '../../../api'
import { languageName } from '../../../utils'
import { useTranslation } from 'react-i18next'
import { TemplateContext } from '../../../contexts'
import { LocaleOption, Template } from '../../../types'
import { localeOption } from '../template/TemplateContextProvider'

interface EditLocalesParams {
    open: boolean
    setIsOpen: (state: boolean) => void
    setAddOpen: (state: boolean) => void
}

interface LocaleRow {
    locale: LocaleOption
    templates: Template[]
    archived: boolean
}

export default function EditLocalesModal({ open, setIsOpen, setAddOpen }: EditLocalesParams) {
    const { t } = useTranslation()
    const {
        campaign,
        setCampaign,
        currentTemplate,
        locales,
        setLocale,
        setTemplate,
        variantMap,
    } = useContext(TemplateContext)
    const [archivedTemplates, setArchivedTemplates] = useState<Template[]>([])

    const loadArchived = async () => {
        const templates = await api.campaigns.templates(campaign.project_id, campaign.id, true)
        setArchivedTemplates(templates.filter(template => template.deleted_at))
    }

    useEffect(() => {
        if (!open) return
        loadArchived()
    }, [open, campaign.id, campaign.project_id])

    const activeLocales = new Set(locales.map(locale => locale.key))
    const archivedRows = archivedTemplates
        .filter(template => !activeLocales.has(template.locale))
        .reduce<LocaleRow[]>((rows, template) => {
            const row = rows.find(row => row.locale.key === template.locale)
            row
                ? row.templates.push(template)
                : rows.push({ locale: localeOption(template.locale), templates: [template], archived: true })
            return rows
        }, [])

    const items: LocaleRow[] = [
        ...locales.map(locale => ({
            locale,
            templates: variantMap[locale.key] ?? [],
            archived: false,
        })),
        ...archivedRows,
    ]

    const handleRemoveLocale = async ({ templates }: LocaleRow) => {
        const templateIds = templates.filter(template => !template.deleted_at).map(template => template.id)
        if (!templateIds.length) return
        if (!confirm(t('remove_locale_warning'))) return
        await Promise.all(templateIds.map(async id => await api.templates.delete(campaign.project_id, id)))

        setCampaign(prev => ({ ...prev, templates: prev.templates.filter(template => !templateIds.includes(template.id)) }))
        if (currentTemplate && templateIds.includes(currentTemplate.id)) {
            const remaining = campaign.templates.filter(template => !templateIds.includes(template.id) && !template.deleted_at)
            setTemplate(remaining[0])
        }
        await loadArchived()
    }

    const handleDeleteLocalePermanently = async ({ templates }: LocaleRow) => {
        const templateIds = templates.filter(template => template.deleted_at).map(template => template.id)
        if (!templateIds.length) return
        if (!confirm(t('permanently_delete_locale_warning'))) return
        await Promise.all(templateIds.map(async id => await api.templates.delete(campaign.project_id, id)))
        await loadArchived()
    }

    const handleRestoreLocale = async ({ templates }: LocaleRow) => {
        const restored = await Promise.all(
            templates.map(async template => await api.templates.unarchive(campaign.project_id, template.id)),
        )

        setCampaign(prev => ({ ...prev, templates: [...prev.templates, ...restored] }))
        setArchivedTemplates(prev => prev.filter(template => !restored.some(item => item.id === template.id)))
        setTemplate(restored[0])
    }

    return (
        <Modal title={t('translations')}
            description={t('translations_description')}
            open={open}
            onClose={() => setIsOpen(false)}>
            <DataTable
                items={items}
                itemKey={({ item }) => item.locale.key}
                onSelectRow={(item) => {
                    if (item.archived) return
                    setLocale(item.locale.key)
                    setIsOpen(false)
                }}
                columns={[
                    {
                        key: 'label',
                        title: t('language'),
                        cell: ({ item }) => languageName(item.locale.key),
                    },
                    {
                        key: 'key',
                        title: t('locale'),
                        cell: ({ item }) => item.locale.key,
                    },
                    {
                        key: 'status',
                        title: t('status'),
                        cell: ({ item }) => item.archived
                            ? <Tag variant="plain" size="tiny">{t('archived')}</Tag>
                            : <Tag variant="success" size="tiny">{t('active')}</Tag>,
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => item.archived
                            ? (
                                <Menu size="small">
                                    <MenuItem icon={<RestartIcon />} onClick={async () => await handleRestoreLocale(item)}>
                                        {t('restore')}
                                    </MenuItem>
                                    <MenuItem icon={<TrashIcon />} onClick={async () => await handleDeleteLocalePermanently(item)}>
                                        {t('delete')}
                                    </MenuItem>
                                </Menu>
                            )
                            : (
                                <Button
                                    size="small"
                                    variant="destructive"
                                    onClick={async (event) => {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        await handleRemoveLocale(item)
                                    }}>
                                    {t('archive')}
                                </Button>
                            ),
                    },
                ]} />
            <div className="modal-footer">
                <Button size="small" onClick={() => setAddOpen(true)}>{t('add_translation')}</Button>
            </div>
        </Modal>
    )
}
